import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Upload, Trash2, Image, Briefcase, RefreshCw, CheckCircle, Type, Save } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const BrandingSettings = () => {
  const [logo, setLogo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [appName, setAppName] = useState('Workforce OS');
  const [originalAppName, setOriginalAppName] = useState('Workforce OS');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      // Fetch logo
      const logoRes = await axios.get(`${API_URL}/api/settings/logo`);
      if (logoRes.data.has_logo) {
        setLogo(logoRes.data);
        setPreviewUrl(logoRes.data.logo_data);
      } else {
        setLogo(null);
        setPreviewUrl(null);
      }
      
      // Fetch app name
      const nameRes = await axios.get(`${API_URL}/api/settings/app-name`);
      const name = nameRes.data.app_name || 'Workforce OS';
      setAppName(name);
      setOriginalAppName(name);
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, SVG, or WebP.');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB.');
      return;
    }

    // Read file and convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!previewUrl || previewUrl === logo?.logo_data) {
      toast.error('Please select a new logo to upload.');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('wos_token');
      await axios.post(
        `${API_URL}/api/admin/settings/logo`,
        {
          logo_data: previewUrl,
          filename: fileInputRef.current?.files[0]?.name || 'logo',
          content_type: fileInputRef.current?.files[0]?.type || 'image/png'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Logo updated successfully!');
      fetchBranding();
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('branding-updated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!logo) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('wos_token');
      await axios.delete(`${API_URL}/api/admin/settings/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Logo deleted. Default logo will be used.');
      setLogo(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('branding-updated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete logo');
    } finally {
      setDeleting(false);
    }
  };

  const handleReset = () => {
    setPreviewUrl(logo?.logo_data || null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSaveAppName = async () => {
    if (!appName.trim()) {
      toast.error('App name cannot be empty.');
      return;
    }

    if (appName.trim() === originalAppName) {
      toast.info('No changes to save.');
      return;
    }

    setSavingName(true);
    try {
      const token = localStorage.getItem('wos_token');
      await axios.post(
        `${API_URL}/api/admin/settings/app-name?name=${encodeURIComponent(appName.trim())}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('App name updated successfully!');
      setOriginalAppName(appName.trim());
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('branding-updated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update app name');
    } finally {
      setSavingName(false);
    }
  };

  return (
    <DashboardLayout title="Branding Settings" subtitle="Customize your app's logo and branding">
      <div className="max-w-2xl space-y-6">
        
        {/* App Name Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Type className="h-5 w-5" />
              App Name
            </CardTitle>
            <CardDescription>
              Customize the name displayed throughout the application. This will appear in the sidebar, login page, and landing page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="app-name">Application Name</Label>
              <div className="flex gap-3">
                <Input
                  id="app-name"
                  type="text"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  placeholder="Enter app name"
                  maxLength={50}
                  className="flex-1"
                  data-testid="app-name-input"
                />
                <Button
                  onClick={handleSaveAppName}
                  disabled={savingName || appName.trim() === originalAppName}
                  className="gap-2"
                  data-testid="save-app-name-btn"
                >
                  {savingName ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {savingName ? 'Saving...' : 'Save'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Maximum 50 characters. Current: {appName.length}/50
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Logo Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              App Logo
            </CardTitle>
            <CardDescription>
              Upload a custom logo for your application. This logo will appear in the sidebar, login page, and landing page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current/Preview Logo */}
            <div className="space-y-4">
              <Label>Current Logo</Label>
              <div className="flex items-center gap-6">
                <div className="w-32 h-32 border-2 border-dashed border-border rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden">
                  {loading ? (
                    <RefreshCw className="h-8 w-8 text-muted-foreground animate-spin" />
                  ) : previewUrl ? (
                    <img 
                      src={previewUrl} 
                      alt="Logo preview" 
                      className="max-w-full max-h-full object-contain p-2"
                    />
                  ) : (
                    <div className="text-center p-4">
                      <Briefcase className="h-10 w-10 text-primary mx-auto mb-2" />
                      <span className="text-xs text-muted-foreground">Default Logo</span>
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-3">
                  <div className="text-sm text-muted-foreground">
                    {logo ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span>Custom logo active</span>
                        </div>
                        {logo.filename && (
                          <p>File: {logo.filename}</p>
                        )}
                        {logo.updated_at && (
                          <p>Updated: {new Date(logo.updated_at).toLocaleString()}</p>
                        )}
                      </div>
                    ) : (
                      <p>No custom logo uploaded. Using default.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Section */}
            <div className="space-y-4 pt-4 border-t">
              <Label htmlFor="logo-upload">Upload New Logo</Label>
              <div className="flex items-center gap-4">
                <Input
                  ref={fileInputRef}
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleFileSelect}
                  className="flex-1"
                  data-testid="logo-file-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Accepted formats: PNG, JPG, SVG, WebP. Maximum size: 2MB. 
                Recommended size: 200x200 pixels.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t">
              <Button
                onClick={handleUpload}
                disabled={uploading || !previewUrl || previewUrl === logo?.logo_data}
                className="gap-2"
                data-testid="upload-logo-btn"
              >
                {uploading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {uploading ? 'Uploading...' : 'Save Logo'}
              </Button>

              {previewUrl && previewUrl !== logo?.logo_data && (
                <Button
                  variant="outline"
                  onClick={handleReset}
                  data-testid="reset-logo-btn"
                >
                  Reset
                </Button>
              )}

              {logo && (
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="gap-2 ml-auto"
                  data-testid="delete-logo-btn"
                >
                  {deleting ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  {deleting ? 'Deleting...' : 'Delete Logo'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preview in Context */}
        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>See how your branding will appear in the app</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Sidebar Preview */}
              <div className="p-4 bg-white border rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Sidebar Header</p>
                <div className="flex items-center gap-2">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Logo" className="h-8 w-8 object-contain" />
                  ) : (
                    <Briefcase className="h-6 w-6 text-primary" />
                  )}
                  <span className="text-lg font-bold text-primary" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    {appName || 'Workforce OS'}
                  </span>
                </div>
              </div>

              {/* Landing Page Preview */}
              <div className="p-4 bg-slate-900 rounded-lg">
                <p className="text-xs text-slate-400 mb-2">Landing Page Navbar</p>
                <div className="flex items-center gap-2">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Logo" className="h-10 w-10 object-contain rounded-xl" />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{(appName || 'W')[0]}</span>
                    </div>
                  )}
                  <span className="font-bold text-xl text-white">
                    {appName || 'Workforce OS'}
                  </span>
                </div>
              </div>

              {/* Login Page Preview */}
              <div className="p-4 bg-slate-100 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Login Page</p>
                <div className="flex items-center justify-center gap-3">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Logo" className="h-12 w-12 object-contain" />
                  ) : (
                    <Briefcase className="h-10 w-10 text-primary" />
                  )}
                  <span className="text-3xl font-bold text-primary" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    {appName || 'Workforce OS'}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default BrandingSettings;
